import type { SelectInternalTerminal } from '@workspace/db/extra'
import { useEffect, useState } from 'react'
import { apiErrorMessage } from '../../../features/locations/api-errors'
import { useLocation } from '../../../features/locations/use-location'
import {
  cashDrawerError,
  useAssignTerminal,
  useCashDrawerConfig,
  useLocationTerminals,
  usePrinterConfig,
  useSetupCashDrawer,
  useSetupReceiptPrinter,
  useTerminalSearch,
  useUnassignCashDrawer,
  useUnassignReceiptPrinter,
  useUnassignTerminal,
} from '../../../features/locations/use-pos-terminals'
import { useIdOrCode } from '../../use-id-or-code'
import { Button, ConfirmDialog, Dialog, EmptyRow, Field, Inline, inputCls, Select, SubCrumb, td, th } from './controls'
import { Badge, Card, mono, Notice, PageHeader, SkeletonCards, type Tone } from './ui'

const COOLDOWN_S = 90
const termTone = (s: string | null): Tone => (s === 'online' ? 'ok' : s === 'offline' ? 'bad' : 'off')

/** Client-side stand-in for v1's 90s per-workstation KV cooldown (the server lock is not built in v2). */
function useCooldown() {
  const [until, setUntil] = useState(0)
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (until <= Date.now()) return
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [until])
  return {
    left: Math.max(0, Math.ceil((until - now) / 1000)),
    start: () => {
      setNow(Date.now())
      setUntil(Date.now() + COOLDOWN_S * 1000)
    },
  }
}

function AssignDialog({
  idOrCode,
  storeCode,
  banner,
  assignedIds,
  onClose,
}: {
  idOrCode: string
  storeCode: string
  banner: string
  assignedIds: Set<string>
  onClose: () => void
}) {
  const [draft, setDraft] = useState('')
  const [q, setQ] = useState('')
  const search = useTerminalSearch(banner, q)
  const assign = useAssignTerminal()
  const hits = (search.data ?? []).filter((t) => !assignedIds.has(t.id))

  return (
    <Dialog
      open
      title="Assign a terminal"
      onClose={onClose}
      footer={
        <Button variant="quiet" onClick={onClose}>
          Done
        </Button>
      }
    >
      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault()
          setQ(draft.trim())
        }}
      >
        <input
          className={inputCls}
          placeholder="Search by terminal name"
          aria-label="Search terminals"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
        />
        <Button type="submit" disabled={draft.trim() === ''}>
          Search
        </Button>
      </form>
      {search.isFetching ? <Inline tone="muted">Searching...</Inline> : null}
      {search.error ? <Inline tone="bad">{apiErrorMessage(search.error)}</Inline> : null}
      {q && !search.isFetching && hits.length === 0 ? (
        <Inline tone="muted">No unassigned terminals match.</Inline>
      ) : null}
      <ul className="grid gap-1">
        {hits.map((t) => (
          <li
            key={t.id}
            className="flex items-center justify-between gap-3 rounded-[10px] bg-[var(--color-surface-2)] px-3 py-2"
          >
            <div className="leading-tight">
              <div className="text-[14px] font-medium">{t.name}</div>
              <div className="text-[12px] text-[var(--color-muted)]">
                {[t.model, t.serialNumber].filter(Boolean).join(' · ')}
              </div>
            </div>
            <Button
              disabled={assign.isPending}
              onClick={() =>
                assign.mutate({ idOrCode, terminalId: t.id, storeCode }, { onSuccess: () => setQ((x) => x) })
              }
            >
              Assign
            </Button>
          </li>
        ))}
      </ul>
      {assign.error ? <Inline tone="bad">{apiErrorMessage(assign.error)}</Inline> : null}
      {assign.isSuccess ? <Inline tone="ok">Terminal assigned.</Inline> : null}
    </Dialog>
  )
}

const PRINTER_MODELS: [string, string][] = [
  ['TM-H6000IV', 'TM-H6000IV (USB)'],
  ['TM-88V', 'TM-88V (Network)'],
  ['Toshiba6145', 'Toshiba 6145'],
]

function PeripheralsDialog({
  code,
  terminal,
  onClose,
}: {
  code: string
  terminal: SelectInternalTerminal
  onClose: () => void
}) {
  const ws = terminal.workstationId ?? null
  const printer = usePrinterConfig(code, ws)
  const drawer = useCashDrawerConfig(code, ws)
  const setupPrinter = useSetupReceiptPrinter()
  const unassignPrinter = useUnassignReceiptPrinter()
  const setupDrawer = useSetupCashDrawer()
  const unassignDrawer = useUnassignCashDrawer()
  const cooldown = useCooldown()

  const [model, setModel] = useState('')
  const [mac, setMac] = useState('')
  const [size, setSize] = useState<'' | 'large' | 'small'>('')
  const [ptype, setPtype] = useState<'' | 'epson' | 'toshiba'>('')
  const [logical, setLogical] = useState<'CashDrawer1' | 'CashDrawer2' | 'CashDrawer3' | 'CashDrawer4'>('CashDrawer1')
  const [smallOk, setSmallOk] = useState(false)

  const locked = cooldown.left > 0
  const printerValid = model !== '' && (model !== 'TM-88V' || mac.trim() !== '')
  const drawerRuleError = size ? cashDrawerError({ drawerSize: size, printerType: ptype }) : null
  const drawerValid = size !== '' && !drawerRuleError && (size !== 'small' || smallOk)

  if (!ws)
    return (
      <Dialog
        open
        title={`Peripherals - ${terminal.name}`}
        onClose={onClose}
        footer={
          <Button variant="quiet" onClick={onClose}>
            Close
          </Button>
        }
      >
        <p className="text-[14px] text-[var(--color-muted)]">
          This terminal has no workstation number yet, so a receipt printer or cash drawer can't be configured. Register
          it as a workstation first.
        </p>
      </Dialog>
    )

  return (
    <Dialog
      open
      title={`Peripherals - ${terminal.name}`}
      onClose={onClose}
      footer={
        <Button variant="quiet" onClick={onClose}>
          Close
        </Button>
      }
    >
      <p className="text-[13px] text-[var(--color-muted)]">
        Workstation <span style={mono}>{ws}</span>. Changes apply asynchronously
        {locked ? `; further changes unlock in ${cooldown.left}s.` : '.'}
      </p>

      <fieldset className="grid gap-3 rounded-[10px] border border-[var(--color-border)] p-4">
        <legend className="px-1 text-[13px] font-semibold">Receipt printer</legend>
        {printer.isLoading ? (
          <Inline tone="muted">Loading...</Inline>
        ) : printer.data?.hasPrinter ? (
          <div className="flex items-center justify-between gap-3">
            <div className="text-[14px]">
              <Badge label={printer.data.printerModel ?? 'Configured'} tone="ok" />
              {printer.data.printerIp ? (
                <span className="ml-2 text-[var(--color-muted)]" style={mono}>
                  {printer.data.printerIp}
                </span>
              ) : null}
            </div>
            <Button
              variant="danger"
              disabled={locked || unassignPrinter.isPending}
              onClick={() => unassignPrinter.mutate({ code, workstationId: ws }, { onSuccess: cooldown.start })}
            >
              {unassignPrinter.isPending ? 'Removing...' : 'Unassign'}
            </Button>
          </div>
        ) : (
          <>
            <Select value={model} onChange={setModel} options={PRINTER_MODELS} placeholder="Select a model" />
            {model === 'TM-88V' ? (
              <Field label="Printer MAC address">
                <input className={inputCls} value={mac} onChange={(e) => setMac(e.target.value)} style={mono} />
              </Field>
            ) : null}
            <div>
              <Button
                disabled={!printerValid || locked || setupPrinter.isPending}
                onClick={() =>
                  setupPrinter.mutate(
                    {
                      code,
                      workstationId: ws,
                      printerModel: model,
                      printerMac: model === 'TM-88V' ? mac.trim() : undefined,
                    },
                    { onSuccess: cooldown.start },
                  )
                }
              >
                {setupPrinter.isPending ? 'Setting up...' : 'Set up printer'}
              </Button>
            </div>
          </>
        )}
        {printer.error ? <Inline tone="bad">{apiErrorMessage(printer.error)}</Inline> : null}
        {setupPrinter.error ? <Inline tone="bad">{apiErrorMessage(setupPrinter.error)}</Inline> : null}
        {unassignPrinter.error ? <Inline tone="bad">{apiErrorMessage(unassignPrinter.error)}</Inline> : null}
      </fieldset>

      <fieldset className="grid gap-3 rounded-[10px] border border-[var(--color-border)] p-4">
        <legend className="px-1 text-[13px] font-semibold">Cash drawer</legend>
        {drawer.isLoading ? (
          <Inline tone="muted">Loading...</Inline>
        ) : drawer.data?.hasCashDrawer ? (
          <div className="flex items-center justify-between gap-3">
            <div className="text-[14px]">
              <Badge
                label={`${drawer.data.drawerSize ?? 'Configured'}${drawer.data.logicalName ? ` · ${drawer.data.logicalName}` : ''}`}
                tone="ok"
              />
            </div>
            <Button
              variant="danger"
              disabled={locked || unassignDrawer.isPending}
              onClick={() => unassignDrawer.mutate({ code, workstationId: ws }, { onSuccess: cooldown.start })}
            >
              {unassignDrawer.isPending ? 'Removing...' : 'Unassign'}
            </Button>
          </div>
        ) : (
          <>
            <Select
              value={size}
              onChange={(v) => setSize(v as '' | 'large' | 'small')}
              options={[
                ['large', 'Large'],
                ['small', 'Small'],
              ]}
              placeholder="Select a size"
            />
            {size === 'large' ? (
              <>
                <Select
                  value={ptype}
                  onChange={(v) => setPtype(v as '' | 'epson' | 'toshiba')}
                  options={[
                    ['epson', 'Epson'],
                    ['toshiba', 'Toshiba'],
                  ]}
                  placeholder="Printer type"
                />
                <Select
                  value={logical}
                  onChange={(v) => setLogical(v as typeof logical)}
                  options={(['CashDrawer1', 'CashDrawer2', 'CashDrawer3', 'CashDrawer4'] as const).map((n) => [n, n])}
                />
              </>
            ) : null}
            {size === 'small' ? (
              <label className="flex items-start gap-2 text-[13px] text-[var(--color-muted)]">
                <input
                  type="checkbox"
                  className="mt-0.5 size-4 accent-[var(--color-accent)]"
                  checked={smallOk}
                  onChange={(e) => setSmallOk(e.target.checked)}
                />
                I confirmed the device number is "3" in POS Control Center.
              </label>
            ) : null}
            {drawerRuleError ? <Inline tone="muted">{drawerRuleError}</Inline> : null}
            <div>
              <Button
                disabled={!drawerValid || locked || setupDrawer.isPending}
                onClick={() =>
                  size &&
                  setupDrawer.mutate(
                    {
                      code,
                      workstationId: ws,
                      drawerSize: size,
                      printerType: size === 'large' && ptype ? ptype : undefined,
                      logicalName: size === 'large' ? logical : undefined,
                    },
                    { onSuccess: cooldown.start },
                  )
                }
              >
                {setupDrawer.isPending ? 'Setting up...' : 'Set up cash drawer'}
              </Button>
            </div>
          </>
        )}
        {drawer.error ? <Inline tone="bad">{apiErrorMessage(drawer.error)}</Inline> : null}
        {setupDrawer.error ? <Inline tone="bad">{apiErrorMessage(setupDrawer.error)}</Inline> : null}
        {unassignDrawer.error ? <Inline tone="bad">{apiErrorMessage(unassignDrawer.error)}</Inline> : null}
      </fieldset>
    </Dialog>
  )
}

export function PorcelainPosTerminals() {
  const idOrCode = useIdOrCode()
  const location = useLocation(idOrCode)
  const terminals = useLocationTerminals(idOrCode)
  const unassign = useUnassignTerminal()

  const [assignOpen, setAssignOpen] = useState(false)
  const [periph, setPeriph] = useState<SelectInternalTerminal | null>(null)
  const [removing, setRemoving] = useState<SelectInternalTerminal | null>(null)

  const header = (
    <PageHeader
      eyebrow={<SubCrumb idOrCode={idOrCode} section="pos" page="Terminals" />}
      title="Terminals"
      subtitle="Payment terminals assigned to this store, with their receipt printer and cash drawer."
    />
  )

  if (location.isLoading || terminals.isLoading)
    return (
      <section>
        {header}
        <SkeletonCards rows={2} />
      </section>
    )
  const loc = location.data
  if (location.error || terminals.error || !loc)
    return (
      <section>
        {header}
        <Notice
          tone="error"
          title="We couldn't load terminals"
          body="Check your connection and try again in a moment."
        />
      </section>
    )

  const list = terminals.data ?? []
  const storeCode = loc.aptosStoreCode ?? loc.code ?? idOrCode
  // Printer/drawer `code` is passed bare (the location code), as v1's terminals route does with loaderData.data.code.
  const posCode = loc.code ?? storeCode

  return (
    <section>
      {header}
      <div className="mt-8">
        <Card
          title={`${list.length} terminal${list.length === 1 ? '' : 's'}`}
          aside={<Button onClick={() => setAssignOpen(true)}>Assign terminal</Button>}
        >
          {list.length === 0 ? (
            <EmptyRow>No terminals are assigned to this store yet.</EmptyRow>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-[14px]">
                <thead>
                  <tr>
                    <th className={th}>Terminal</th>
                    <th className={th}>Status</th>
                    <th className={th}>Workstation</th>
                    <th className={th}>IP</th>
                    <th className={th}>Firmware</th>
                    <th className={`${th} text-right`}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((t) => (
                    <tr key={t.id} className="transition-colors hover:bg-[var(--v1-accent-soft)]">
                      <td className={td}>
                        <div className="leading-tight">
                          <div className="font-medium">{t.name}</div>
                          <div className="text-[12px] text-[var(--color-muted)]">{t.model ?? '-'}</div>
                        </div>
                      </td>
                      <td className={td}>
                        <Badge label={t.status ?? 'unknown'} tone={termTone(t.status)} />
                      </td>
                      <td className={td} style={mono}>
                        {t.workstationId ?? '-'}
                      </td>
                      <td className={td} style={mono}>
                        {t.ethernetIpAddress ?? t.wifiIpAddress ?? '-'}
                      </td>
                      <td className={`${td} text-[var(--color-muted)]`}>{t.firmwareVersion ?? '-'}</td>
                      <td className={`${td} text-right`}>
                        <div className="flex justify-end gap-1">
                          <Button variant="quiet" onClick={() => setPeriph(t)}>
                            Peripherals
                          </Button>
                          <Button variant="danger" onClick={() => setRemoving(t)}>
                            Unassign
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {assignOpen ? (
        <AssignDialog
          idOrCode={idOrCode}
          storeCode={storeCode}
          banner={loc.banner ?? ''}
          assignedIds={new Set(list.map((t) => t.id))}
          onClose={() => setAssignOpen(false)}
        />
      ) : null}
      {periph ? <PeripheralsDialog code={posCode} terminal={periph} onClose={() => setPeriph(null)} /> : null}
      <ConfirmDialog
        open={removing !== null}
        title={`Unassign ${removing?.name ?? 'terminal'}?`}
        body="The terminal is removed from this store and can be assigned elsewhere. Its printer and cash drawer setup is not changed."
        confirmLabel="Unassign"
        danger
        pending={unassign.isPending}
        error={unassign.error ? apiErrorMessage(unassign.error) : null}
        onConfirm={() =>
          removing && unassign.mutate({ idOrCode, terminalId: removing.id }, { onSuccess: () => setRemoving(null) })
        }
        onClose={() => {
          unassign.reset()
          setRemoving(null)
        }}
      />
    </section>
  )
}
